import { TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'

import type { Function } from './function'

export interface Functionalization {
  functionId: Function.Id
  percent: number
}
export namespace Functionalization {
  /** Check if sum of functionalizations percents is 1 */
  export function isFullyCoded(
    functionalizations: readonly Functionalization[],
  ) {
    return calculateTotal(functionalizations) === 1
  }

  /**
   * Calculate total percent of functionalizations. Note that due to floating
   * point accumulation, the total may not be exactly 1 even if the
   * functionalizations are fully coded. Therefore, the total is rounded to 2
   * decimal places before returning.
   */
  export function calculateTotal(
    functionalizations: readonly Functionalization[],
  ) {
    const total = functionalizations.reduce(
      (prev, curr) => prev + curr.percent,
      0,
    )
    return Math.round((total + Number.EPSILON) * 100) / 100
  }

  /** Check if functionalizations is empty or all percents are 0 */
  export function isEmpty(functionalizations: readonly Functionalization[]) {
    return (
      functionalizations.length === 0 ||
      functionalizations.every(f => f.percent === 0)
    )
  }

  export const isFunctionalization: TypeChecker<Functionalization> =
    TypeChecker.Factory.object({
      functionId: core.dms.data.Record.Id.isId,
      percent: TypeChecker.isNumber,
    })

  /** Functionalizations of many Space Records, keyed by Space Record id */
  export type Lookup = globalThis.Record<
    core.dms.data.Record.Id,
    Functionalization[]
  >

  export type Weighted = Omit<Functionalization, 'percent'> & {
    weight: number
  }
  export namespace Weighted {
    /**
     * Merge functionalizations with the same functionId by summing their weights.
     * This is useful when converting from weighted functionalizations to percentage-based functionalizations,
     * as there may be multiple entries for the same functionId that need to be combined before calculating percentages.
     */
    export function merge(weightedFunctionalizations: Weighted[]): Weighted[] {
      const merged: Weighted[] = []
      for (const functionalization of weightedFunctionalizations) {
        const existing = merged.find(
          ({ functionId }) => functionId === functionalization.functionId,
        )
        if (existing) {
          existing.weight += functionalization.weight
        } else {
          merged.push({ ...functionalization })
        }
      }
      return merged
    }
  }

  /**
   * Convert an array of weighted functionalizations to an array of functionalizations with percentages.
   */
  export function fromWeighted(
    weightedFunctionalizations: Weighted[],
  ): Functionalization[] {
    const mergedWeightedFunctionalizations = Weighted.merge(
      weightedFunctionalizations,
    )
    // Sum total weight for normalization
    const totalWeight = mergedWeightedFunctionalizations.reduce(
      (sum, mapping) => sum + mapping.weight,
      0,
    )
    // Normalize weights to get percentages
    return mergedWeightedFunctionalizations.map(mapping => ({
      functionId: mapping.functionId,
      percent: totalWeight > 0 ? mapping.weight / totalWeight : 0,
    }))
  }
}
